import { isNil, findLastIndex } from 'lodash';

import {
  ApplicationStep,
  Category,
  CategoryCode,
  CategoryStatus,
  CategoryStatusInputModel,
  SubscriptionPlan,
} from '@acorn/util';

export const findLatestStepIndex = (
  categories: Category[],
  onlyAboutYou: boolean = false
): [number | undefined, number | undefined] => {
    //prod4
//   const firstPendingCategoryIndex = categories.findIndex(
//     (category) => category.status === CategoryStatus.Pending
//   );
//   if (firstPendingCategoryIndex === 0) {
//     return [undefined, undefined];
//   }

  const pendingCategoryIndex = categories.findIndex(
    (category) =>
      category.status === CategoryStatus.InProgress ||
      category.status === CategoryStatus.Pending
  );

  if (pendingCategoryIndex < 0) {
    // all categories have been completed
    if (onlyAboutYou) {
      const completedCategoryIndex = categories.findIndex(
        (category) => category.status === CategoryStatus.Complete
      );

      // Ensure that the completedCategoryIndex is valid and the category has childItems
      const completedSubCategoryIndex = categories[
        completedCategoryIndex < 0 ? 0 : completedCategoryIndex
      ]?.childItems?.findIndex(
        (subcategory) => subcategory.status === CategoryStatus.Complete
      ) ?? -1;

      return [
        completedCategoryIndex < 0 ? 0 : completedCategoryIndex,
        completedSubCategoryIndex < 0 ? 0 : completedSubCategoryIndex,
      ];
    }
    const completedCategoryIndex = findLastIndex(
      categories,
      (category) => category.status === CategoryStatus.Complete
    );

    const completedSubCategoryIndex = findLastIndex(
      categories[completedCategoryIndex < 0 ? 0 : completedCategoryIndex]
        .childItems,
      (category) => category.status === CategoryStatus.Complete
    );

    return [
      completedCategoryIndex < 0 ? 0 : completedCategoryIndex,
      completedSubCategoryIndex < 0 ? 0 : completedSubCategoryIndex,
    ];
  }

  let pendingSubCategoryIndex:any = 0;

  if(categories[pendingCategoryIndex].categoryCode === "RiskToleranceOutcome"){
    pendingSubCategoryIndex = 0;
  }else{
     pendingSubCategoryIndex = categories[
        pendingCategoryIndex
      ].childItems?.findIndex(
        (subCategory) => subCategory.status === CategoryStatus.Pending
      );

  }

  if (isNil(pendingSubCategoryIndex) || pendingSubCategoryIndex < 0) {
    return [0, 0];
  }

  return [pendingCategoryIndex, pendingSubCategoryIndex];
};

// export const getUpdatedCategoryStatus = (
//   categories: Category[],
//   selectedParentIndex: number,
//   selectedIndex: number
// ): CategoryStatusInputModel[] => {
//   const categoryStatus: CategoryStatusInputModel[] = [];

//   const currentParentCategory = categories[selectedParentIndex];

//   const skipChildCheck = currentParentCategory.categoryCode === 'RiskToleranceOutcome';

//   if (skipChildCheck && currentParentCategory.categoryCode === 'RiskToleranceOutcome') {
//      categoryStatus.push({
//         categoryCode: currentParentCategory.categoryCode,
//         status: CategoryStatus.Complete,
//       });

//       return categoryStatus;
//   }


//   if (!currentParentCategory?.childItems?.length) {
//     return [];
//   }

//   const currentCategory = currentParentCategory.childItems[selectedIndex];

//   if (!currentCategory) {
//     return [];
//   }

//   currentCategory.status = CategoryStatus.Complete;
//   categoryStatus.push({
//     categoryCode: currentCategory.categoryCode,
//     status: CategoryStatus.Complete,
//   });

//   if (currentCategory.isLastItem) {
//     currentParentCategory.status = CategoryStatus.Complete;

//     categoryStatus.push({
//       categoryCode: currentParentCategory.categoryCode,
//       status: CategoryStatus.Complete,
//     });

//     const nextParentCategory = categories[selectedParentIndex + 1];

//     if (nextParentCategory?.status === CategoryStatus.Pending) {
//       nextParentCategory.status = CategoryStatus.InProgress;

//       categoryStatus.push({
//         categoryCode: nextParentCategory.categoryCode,
//         status: CategoryStatus.InProgress,
//       });
//     }
//   } else if (currentParentCategory.status === CategoryStatus.Pending) {
//     categoryStatus.push({
//       categoryCode: currentParentCategory.categoryCode,
//       status: CategoryStatus.InProgress,
//     });
//   }

//   return categoryStatus;
// };

export const getUpdatedCategoryStatus = (
  categories: Category[],
  selectedParentIndex: number,
  selectedIndex: number,
  isDashboardApp: boolean = false
): CategoryStatusInputModel[] => {
  const categoryStatus: CategoryStatusInputModel[] = [];
  const currentParentCategory = categories[selectedParentIndex];

  const skipChildCheck = currentParentCategory.categoryCode === 'RiskToleranceOutcome';
  if (skipChildCheck) {
    categoryStatus.push({
      categoryCode: currentParentCategory.categoryCode,
      status: CategoryStatus.Complete,
    });
    return categoryStatus;
  }

  if (!currentParentCategory?.childItems?.length) return [];

  const currentCategory = currentParentCategory.childItems[selectedIndex];
  if (!currentCategory) return [];

  currentCategory.status = CategoryStatus.Complete;
  categoryStatus.push({
    categoryCode: currentCategory.categoryCode,
    status: CategoryStatus.Complete,
  });

  if (currentCategory.isLastItem) {
    currentParentCategory.status = CategoryStatus.Complete;
    categoryStatus.push({
      categoryCode: currentParentCategory.categoryCode,
      status: CategoryStatus.Complete,
    });

    const nextParentCategory = categories[selectedParentIndex + 1];
    if (nextParentCategory?.status === CategoryStatus.Pending) {
      nextParentCategory.status = CategoryStatus.InProgress;
      categoryStatus.push({
        categoryCode: nextParentCategory.categoryCode,
        status: CategoryStatus.InProgress,
      });
    }
  } else if (currentParentCategory.status === CategoryStatus.Pending) {
    categoryStatus.push({
      categoryCode: currentParentCategory.categoryCode,
      status: CategoryStatus.InProgress,
    });
  }

  if (isDashboardApp) {
    const pushOrUpdate = (targetCode: CategoryCode, checkCodes: string[]) => {
      const hasComplete = categoryStatus.some(
        (item) => checkCodes.includes(item.categoryCode) && item.status === CategoryStatus.Complete
      );

      if (hasComplete) {
        const existingIndex = categoryStatus.findIndex((item) => item.categoryCode === targetCode);
        if (existingIndex !== -1) {
          categoryStatus[existingIndex].status = CategoryStatus.Complete;
        } else {
          categoryStatus.push({
            categoryCode: targetCode,
            status: CategoryStatus.Complete,
          });
        }
      }
    };

    pushOrUpdate(CategoryCode.Liabilities, ['Other']);
    pushOrUpdate(CategoryCode.Assets, ['OtherAssets']);
    pushOrUpdate(CategoryCode.Expenses, ['OtherNonTaxDeductibleExpenses']);
    pushOrUpdate(CategoryCode.Income, ['OtherIncome']);
  }

  return categoryStatus;
};

export function isWealthCoachNotPlan(
  appStep: ApplicationStep | null,
  subscriptionPlan: SubscriptionPlan | undefined
): boolean {
  return (
    (appStep === ApplicationStep.FinancialProjection ||
      appStep === ApplicationStep.WealthCoach) &&
    !subscriptionPlan
  );
}

export function  formatDateOrNull(date: any): string | null {
    if (date) {
        const dt = new Date(date);
        const year = dt.getFullYear();
        const month = String(dt.getMonth() + 1).padStart(2, '0');
        const day = String(dt.getDate()).padStart(2, '0');

        return `${year}-${month}-${day}`;
    }
    return null;
}

export function  calculateAge(dateOfBirth: any): number {
    const birthDate = new Date(dateOfBirth);
    const today = new Date();
    let age = today.getFullYear() - birthDate.getFullYear();
    const monthDifference = today.getMonth() - birthDate.getMonth();
    if (monthDifference < 0 || (monthDifference === 0 && today.getDate() < birthDate.getDate())) {
        age--;
    }
    return age;
}
